export const TAMANHO_MAXIMO_LOGO_EM_BYTES = 5 * 1024 * 1024
export const LIMITE_PIXELS_LOGO = 25_000_000
export const LARGURA_MAXIMA_LOGO = 1200
export const ALTURA_MAXIMA_LOGO = 400
export const MARGEM_RECORTE_LOGO = 4

const LIMITE_FUNDO = 10

export type CodigoErroNormalizacaoLogo =
  | 'formato-invalido'
  | 'arquivo-grande'
  | 'pixels-excedidos'
  | 'sem-conteudo'
  | 'navegador-incompativel'
  | 'falha-processamento'

export const MENSAGENS_ERRO_NORMALIZACAO: Record<CodigoErroNormalizacaoLogo, string> = {
  'formato-invalido': 'Envie uma logo PNG, JPG ou WEBP válida.',
  'arquivo-grande': 'Arquivo muito grande. Envie uma logo de até 5 MB.',
  'pixels-excedidos': 'A logo deve possuir no máximo 25 megapixels.',
  'sem-conteudo': 'A imagem selecionada não possui conteúdo visível.',
  'navegador-incompativel':
    'Seu navegador não oferece suporte à otimização da logo. Atualize-o e tente novamente.',
  'falha-processamento': 'Não foi possível otimizar a logo. Tente outra imagem.',
}

export interface NormalizarLogoRequest {
  id: string
  arquivo: File
}

export type NormalizarLogoResponse =
  | { id: string; success: true; blob: Blob }
  | { id: string; success: false; errorCode: CodigoErroNormalizacaoLogo }

export interface RecorteImagem {
  x: number
  y: number
  width: number
  height: number
}

export interface DimensoesImagem {
  width: number
  height: number
}

type Cor = readonly [number, number, number, number]
type Fundo = { tipo: 'transparente' } | { tipo: 'opaco'; cor: Cor } | null

export function encontrarRecorte(
  pixels: Uint8ClampedArray,
  width: number,
  height: number
): RecorteImagem | null {
  const fundo = identificarFundo(pixels, width, height)
  if (!fundo) return { x: 0, y: 0, width, height }

  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4
      if (!ehConteudo(pixels, offset, fundo)) continue
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x)
      maxY = Math.max(maxY, y)
    }
  }

  if (maxX < 0 || maxY < 0) return null

  const x = Math.max(0, minX - MARGEM_RECORTE_LOGO)
  const y = Math.max(0, minY - MARGEM_RECORTE_LOGO)
  const right = Math.min(width - 1, maxX + MARGEM_RECORTE_LOGO)
  const bottom = Math.min(height - 1, maxY + MARGEM_RECORTE_LOGO)

  return { x, y, width: right - x + 1, height: bottom - y + 1 }
}

export function calcularDimensoesFinais(width: number, height: number): DimensoesImagem {
  const escala = Math.min(1, LARGURA_MAXIMA_LOGO / width, ALTURA_MAXIMA_LOGO / height)
  return {
    width: Math.min(LARGURA_MAXIMA_LOGO, Math.max(1, Math.round(width * escala))),
    height: Math.min(ALTURA_MAXIMA_LOGO, Math.max(1, Math.round(height * escala))),
  }
}

function identificarFundo(
  pixels: Uint8ClampedArray,
  width: number,
  height: number
): Fundo {
  const cantos = [
    lerCor(pixels, 0),
    lerCor(pixels, (width - 1) * 4),
    lerCor(pixels, (height - 1) * width * 4),
    lerCor(pixels, (height * width - 1) * 4),
  ]

  if (cantos.filter((cor) => cor[3] <= LIMITE_FUNDO).length >= 3) {
    return { tipo: 'transparente' }
  }

  for (const referencia of cantos) {
    const concordantes = cantos.filter(
      (cor) => cor[3] > LIMITE_FUNDO && distanciaMaxima(cor, referencia) <= LIMITE_FUNDO
    )
    if (concordantes.length >= 3) {
      return { tipo: 'opaco', cor: medianaDosCanais(concordantes) }
    }
  }

  return null
}

function ehConteudo(pixels: Uint8ClampedArray, offset: number, fundo: Exclude<Fundo, null>) {
  if (fundo.tipo === 'transparente') return pixels[offset + 3] > LIMITE_FUNDO
  return distanciaMaximaNoPixel(pixels, offset, fundo.cor) > LIMITE_FUNDO
}

function lerCor(pixels: Uint8ClampedArray, offset: number): Cor {
  return [pixels[offset], pixels[offset + 1], pixels[offset + 2], pixels[offset + 3]]
}

function distanciaMaxima(a: Cor, b: Cor): number {
  return Math.max(
    Math.abs(a[0] - b[0]),
    Math.abs(a[1] - b[1]),
    Math.abs(a[2] - b[2]),
    Math.abs(a[3] - b[3])
  )
}

function distanciaMaximaNoPixel(
  pixels: Uint8ClampedArray,
  offset: number,
  referencia: Cor
): number {
  return Math.max(
    Math.abs(pixels[offset] - referencia[0]),
    Math.abs(pixels[offset + 1] - referencia[1]),
    Math.abs(pixels[offset + 2] - referencia[2]),
    Math.abs(pixels[offset + 3] - referencia[3])
  )
}

function medianaDosCanais(cores: Cor[]): Cor {
  return [0, 1, 2, 3].map((canal) => {
    const valores = cores.map((cor) => cor[canal]).sort((a, b) => a - b)
    return valores[Math.floor(valores.length / 2)]
  }) as unknown as Cor
}
